import { useContext, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { authContext } from 'contexts/AuthContext';
import { axiosGetAPI } from 'services';
import { APP_TOKEN } from 'constants';

const TASKS_ENDPOINT =
  process.env.REACT_APP_REST_API_ENDPOINT ||
  (process.env.REACT_APP_API_BASE_URL ? `${process.env.REACT_APP_API_BASE_URL}/api/` : '/api/');

const NOTIFIED_TASKS_STORAGE_KEY =
  'polystox-admin-task-ert-reminder-notifications';
const POLL_INTERVAL_MS = 15000;

const normalizeTaskIdentifier = (value) => {
  if (value === null || value === undefined) return '';
  return `${value}`.trim();
};

const getAssignedToIdFromStorage = () => {
  try {
    const stored = window.localStorage.getItem(APP_TOKEN);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return (
      parsed?.userId ||
      parsed?.id ||
      parsed?.user_id ||
      parsed?.user?.id ||
      parsed?.user?.userId ||
      parsed?.staffId ||
      parsed?.staff?.id ||
      null
    );
  } catch (error) {
    return null;
  }
};

const getCurrentUserId = (authData) =>
  normalizeTaskIdentifier(
    authData?.userId ||
      authData?.id ||
      authData?.user_id ||
      authData?.user?.id ||
      authData?.user?.userId ||
      authData?.staffId ||
      authData?.staff?.id ||
      getAssignedToIdFromStorage(),
  );

const parseTimestampToMs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue > 1e12 ? numericValue : numericValue * 1000;
  }

  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const getTaskStatus = (task = {}) => {
  const normalized = `${task?.status || task?.statusKey || ''}`
    .replace(/_/g, ' ')
    .trim()
    .toUpperCase();
  if (normalized === 'COMPLETED' || normalized === 'DONE') return 'completed';
  return 'pending';
};

const getTaskDisplayTitle = (task = {}) =>
  `${task?.title || task?.taskTitle || task?.name || 'Untitled task'}`.trim();

const isDeletedValue = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
};

const getTaskDeadlineMs = (task = {}) => {
  const directTimeCandidates = [
    task?.ertEndTimeMs,
    task?.ertEndTime,
    task?.ertEndAtMs,
    task?.ertEndAt,
    task?.estimatedResolutionEndTime,
  ];

  const directTimeMatch = directTimeCandidates
    .map((candidate) => parseTimestampToMs(candidate))
    .find((parsed) => parsed !== null);

  if (directTimeMatch !== undefined) return directTimeMatch;

  const createdAtMs = parseTimestampToMs(
    task?.createdAtMs ?? task?.createdAtValue ?? task?.createdAt,
  );
  const ertMinutes = Number(task?.ertMinutes);
  if (createdAtMs !== null && Number.isFinite(ertMinutes) && ertMinutes > 0) {
    return createdAtMs + ertMinutes * 60 * 1000;
  }

  return null;
};

const getStorageKey = (currentUserId) =>
  `${NOTIFIED_TASKS_STORAGE_KEY}:${normalizeTaskIdentifier(currentUserId)}`;

const getStoredNotificationMap = (currentUserId) => {
  if (typeof window === 'undefined') return {};

  try {
    const rawValue = window.localStorage.getItem(getStorageKey(currentUserId));
    const parsedValue = JSON.parse(rawValue || '{}');
    return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
      ? parsedValue
      : {};
  } catch (error) {
    console.warn('Unable to read task reminder notifications from storage.', error);
    return {};
  }
};

const saveStoredNotificationMap = (currentUserId, value = {}) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    getStorageKey(currentUserId),
    JSON.stringify(value || {}),
  );
};

const buildTasksUrl = (currentUserId) =>
  `${TASKS_ENDPOINT}tasks?assignedToId=${encodeURIComponent(currentUserId)}`;

const TaskReminderNotificationListener = () => {
  const { auth } = useContext(authContext);
  const notifiedTasksRef = useRef({});
  const inFlightRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const currentUserId = getCurrentUserId(auth?.data);
    if (!auth?.data?.token || !currentUserId) return undefined;
    if (typeof window === 'undefined') return undefined;

    cancelledRef.current = false;
    notifiedTasksRef.current = getStoredNotificationMap(currentUserId);

    const syncExpiredTasks = async () => {
      if (cancelledRef.current || inFlightRef.current) return;
      inFlightRef.current = true;

      try {
        const response = await axiosGetAPI(buildTasksUrl(currentUserId), auth);
        if (!response?.isSuccessful) return;

        const apiTasks = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const now = Date.now();
        const nextNotificationMap = { ...notifiedTasksRef.current };
        let hasChanges = false;

        const activeTaskIds = new Set();

        apiTasks.forEach((task) => {
          const taskId = normalizeTaskIdentifier(task?.id ?? task?.sourceId);
          if (!taskId) return;
          activeTaskIds.add(taskId);

          if (isDeletedValue(task?.isDeleted)) return;
          if (getTaskStatus(task) === 'completed') return;

          const ertDeadlineMs = getTaskDeadlineMs(task);
          if (!ertDeadlineMs || now < ertDeadlineMs) return;

          const notificationKey = `${taskId}:${ertDeadlineMs}`;
          if (nextNotificationMap[taskId] === notificationKey) return;

          nextNotificationMap[taskId] = notificationKey;
          hasChanges = true;

          toast.info(`[Reminder] ${getTaskDisplayTitle(task)}`, {
            toastId: notificationKey,
          });
        });

        Object.keys(nextNotificationMap).forEach((taskId) => {
          if (activeTaskIds.has(taskId)) return;
          delete nextNotificationMap[taskId];
          hasChanges = true;
        });

        if (hasChanges) {
          notifiedTasksRef.current = nextNotificationMap;
          saveStoredNotificationMap(currentUserId, nextNotificationMap);
        }
      } catch (error) {
        console.error('Failed to check task ERT expiry notifications:', error);
      } finally {
        inFlightRef.current = false;
      }
    };

    syncExpiredTasks();
    const intervalId = window.setInterval(syncExpiredTasks, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncExpiredTasks();
      }
    };

    window.addEventListener('focus', syncExpiredTasks);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelledRef.current = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncExpiredTasks);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [auth]);

  return null;
};

export default TaskReminderNotificationListener;
